<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="isCreate ? null : article.title" :back="listPath">
            <v-btn v-if="isShow" color="primary" depressed @click="goEdit">
                <v-icon left>mdi-pencil-outline</v-icon>Edit
            </v-btn>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-text class="pb-0">
                    <div class="d-flex flex-wrap checkbox-row">
                        <v-checkbox v-model="article.isAnouncement" label="Show as announcement" hide-details class="mt-0" />
                        <v-checkbox v-model="article.isPromo" label="Promo article" hide-details class="mt-0" />
                    </div>
                </v-card-text>
                <v-tabs v-model="languageTab" class="px-2 mt-2">
                    <v-tab>Ελληνικά</v-tab>
                    <v-tab>English</v-tab>
                </v-tabs>
                <v-divider />
                <v-card-text>
                    <v-tabs-items v-model="languageTab">
                        <v-tab-item eager>
                            <v-text-field v-model.trim="article.title" :rules="required" label="Title *" outlined />
                            <v-text-field
                                v-if="article.isAnouncement"
                                v-model.trim="article.announcementTitle"
                                label="Announcement title"
                                outlined
                            />
                            <span class="editor-label">Body</span>
                            <editor v-model="article.body" :api-key="tinymceApiKey" :init="editorInit" :disabled="readOnly" />
                        </v-tab-item>
                        <v-tab-item eager>
                            <v-text-field v-model.trim="article.titleEn" label="Title in English" outlined />
                            <v-text-field
                                v-if="article.isAnouncement"
                                v-model.trim="article.announcementTitleEn"
                                label="Announcement title in English"
                                outlined
                            />
                            <span class="editor-label">Body in English</span>
                            <editor v-model="article.bodyEn" :api-key="tinymceApiKey" :init="editorInit" :disabled="readOnly" />
                        </v-tab-item>
                    </v-tabs-items>
                </v-card-text>
            </v-card>

            <v-card outlined class="mb-4">
                <v-card-title class="section-title">Cover image</v-card-title>
                <v-card-text>
                    <image-input v-model="image" :readonly="readOnly" />
                </v-card-text>
            </v-card>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Publish article' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import imageService from '@/services/image-service'
    import articleService from '@/services/article-service'
    import paronApi from '@/utils/paron-api'
    import Editor from '@tinymce/tinymce-vue'
    import formPage from '@/mixins/form-page'
    import imageUpload from '@/mixins/image-upload'
    import PageHeader from '@/components/PageHeader'
    import ImageInput from '@/components/ImageInput'
    import { editorConfig, tinymceApiKey } from '@/utils/editor'
    import { pick } from '@/utils/format'

    // ArticleType rejects unknown fields, so only these may be sent
    const ARTICLE_FIELDS = ['title', 'body', 'isAnouncement', 'announcementTitle', 'titleEn', 'bodyEn', 'announcementTitleEn', 'isPromo']

    // images pasted or inserted into the body are uploaded straight away and referenced by URL
    function uploadInlineImage(blobInfo, success, failure) {
        const formData = new FormData()
        formData.append('file', blobInfo.blob(), blobInfo.filename())
        imageService.uploadNewImage(formData).then(response => {
            success(`${paronApi.apiUrl}/uploads${response.data.image_url}`)
        }).catch(() => failure('The image could not be uploaded'))
    }

    export default {
        name: 'ArticleForm',
        components: { Editor, PageHeader, ImageInput },
        mixins: [formPage, imageUpload],
        data: () => {
            return {
                entityLabel: 'Article',
                tinymceApiKey,
                editorInit: editorConfig({
                    height: 520,
                    images_upload_handler: uploadInlineImage,
                    automatic_uploads: true,
                    file_picker_types: 'image'
                }),
                articleId: null,
                languageTab: 0,
                article: {
                    isAnouncement: false,
                    isPromo: false,
                    title: '',
                    body: '',
                    announcementTitle: '',
                    titleEn: '',
                    bodyEn: '',
                    announcementTitleEn: ''
                }
            }
        },
        created() {
            if (this.isCreate) {
                return
            }
            this.articleId = parseInt(this.$route.params.articleId)
            this.loadingEntity = true
            articleService.getArticleById(this.articleId).then(resp => {
                const article = resp.data.article
                this.article = { ...this.article, ...pick(article, ARTICLE_FIELDS) }
                this.setExistingImage(article.image)
            }).catch(error => {
                this.$notify.error(error, 'Could not load the article')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                let savedId = null
                const payload = pick(this.article, ARTICLE_FIELDS)
                const request = this.isEdit
                    ? articleService.editArticleById(this.articleId, payload)
                    : articleService.createArticle(payload)
                request.then(resp => {
                    savedId = this.isEdit ? this.articleId : resp.data.id
                    return this.saveImage('articleId', savedId, imageService.uploadNewArticleImage)
                }).then(() => {
                    this.$notify.success(this.isCreate ? 'Article published' : 'Article saved')
                    this.goBack()
                }).catch(error => {
                    if (!savedId) {
                        this.$notify.error(error, 'Could not save the article')
                        return
                    }
                    this.$notify.error('The article was saved, but the image could not be saved.')
                    if (this.isCreate) {
                        this.goToEntity(savedId, 'edit')
                    }
                }).finally(() => {
                    this.saving = false
                })
            }
        }
    }
</script>
